import { memo, useState } from "react";
import {
  AlertTriangle,
  Columns3,
  Copy,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { Button } from "Components/ui/button";
import { Input } from "Components/ui/input";
import { Label } from "Components/ui/label";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "Components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "Components/ui/select";
import { cn } from "lib/utils";

// Description fields come from a rich-text editor and are stored as HTML.
// We don't render the markup in table cells (ugly + XSS surface) — strip to
// plain text for display. The raw HTML stays on the row data so promote
// preserves the original formatting.
const HTML_DISPLAY_FIELDS = new Set(["description", "ar_description"]);

const stripHtmlForDisplay = (value) => {
  if (typeof value !== "string" || !value.includes("<")) return value;
  // Use a detached element so we never attach untrusted HTML to the live DOM,
  // and read textContent which is XSS-safe by definition.
  const el = document.createElement("div");
  el.innerHTML = value;
  return (el.textContent || "").trim();
};

// Long-text columns (description / ar_description) used to render their full
// stripped text into an unconstrained cell, which let a single paragraph blow
// the row height up so badly that barely one product fit on screen. Instead we
// clamp the read view to one line at a fixed width (uniform, dense rows) and
// open a roomy popover anchored to the cell for reading/editing the full text.
function DescriptionCell({
  value,
  label,
  isRtl,
  isEditing,
  editValue,
  setEditValue,
  onSave,
  onCancel,
}) {
  const display = stripHtmlForDisplay(String(value ?? ""));
  return (
    <Popover
      open={isEditing}
      // Outside-click / Esc closes the popover — commit the edit, mirroring the
      // old inline textarea's save-on-blur. Cancel/Done null out editingCell
      // first, so this re-fire is a no-op guarded by handleCellSave.
      onOpenChange={(open) => {
        if (!open) onSave();
      }}
    >
      <PopoverAnchor asChild>
        <div
          className="w-[240px] truncate text-sm text-foreground"
          title={display || undefined}
          dir={isRtl ? "rtl" : undefined}
        >
          {display ? (
            display
          ) : (
            <span className="text-muted-foreground/60">—</span>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={6}
        // The anchor cell is only 240px wide and can sit anywhere across a
        // horizontally-scrolled table, so let Radix flip/shift the panel to
        // stay on-screen and keep a 16px gap from every viewport edge.
        avoidCollisions
        collisionPadding={16}
        className="w-[460px] max-w-[calc(100vw-2rem)] p-3"
        // Keep focus on the textarea; without this Radix steals it back to the
        // anchor on open and the autoFocus below is lost.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="space-y-2">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </Label>
          <textarea
            className="form-control popup-input-main w-full"
            rows={8}
            value={editValue}
            dir={isRtl ? "rtl" : undefined}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") onCancel();
            }}
            autoFocus
          />
          <div className="flex justify-end gap-2 pt-0.5">
            <Button size="sm" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button size="sm" onClick={onSave}>
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Inline numeric-input popover used by the *Add row* and *Duplicate* buttons.
 * Encapsulates the open/value state so the table component doesn't accumulate
 * popover plumbing for each variant.
 */
function RowCountPopover({ trigger, label, confirmLabel }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(1);

  const close = () => {
    setOpen(false);
    setCount(1);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-52 p-3" align="start">
        <div className="space-y-2">
          <Label className="text-xs">{label}</Label>
          <Input
            type="number"
            min="1"
            max="20"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            autoFocus
          />
          <Button
            size="sm"
            className="w-full"
            onClick={() => {
              confirmLabel.onConfirm(count);
              close();
            }}
          >
            {confirmLabel.text(count)}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const PRODUCT_OPTION_TYPE_OPTIONS = [
  { value: "Product", label: "Product" },
  { value: "Varient", label: "Varient" },
];

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const B2B_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

// is_b2b flows through the pipeline in mixed shapes — DB int 1/0, CSV "yes"/"no",
// bulk-edit booleans. Coerce to the canonical "yes"/"no" that both the Select
// (for highlighting the current item) and the submit normalizer expect, so the
// dropdown reflects the right state regardless of source format.
const coerceB2b = (v) => {
  if (v === 1 || v === "1" || v === true || v === "yes" || v === "true") return "yes";
  if (v === 0 || v === "0" || v === false || v === "no" || v === "false") return "no";
  return "";
};

// Columns whose inline editor is a dropdown. The menu portals to document.body
// via Radix, so it can't be clipped by the table's overflow context.
const SELECT_OPTION_GETTERS = {
  brand: (ctx) => ctx.brandOptions,
  occasion: (ctx) => ctx.occasionOptions,
  color: (ctx) => ctx.colorOptions,
  size: (ctx) => ctx.sizeOptions,
  type: (ctx) => ctx.typeOptions,
  sub_category: (ctx) => ctx.subCategoryOptions,
  product_option_type: () => PRODUCT_OPTION_TYPE_OPTIONS,
  status: () => STATUS_OPTIONS,
  is_b2b: () => B2B_OPTIONS,
};

// product_option_type and status always require a value; everything else can
// be cleared via the "— None —" item.
const CLEARABLE_SELECT_COLUMNS = new Set([
  "brand",
  "occasion",
  "color",
  "size",
  "type",
  "sub_category",
]);

// Radix Select disallows "" as an item value, so the clear action uses a
// sentinel string that we translate back to "" before saving.
const SELECT_CLEAR_SENTINEL = "__clear__";

const PendingProductsTable = ({
  products,
  columns,
  visibleColumns,
  selectedRows,
  selectedCount,
  isLoadingData,
  validationErrors,
  rowErrors = new Map(),
  requiredFields,
  brandOptions,
  occasionOptions,
  colorOptions,
  sizeOptions,
  categoryOptions,
  typeOptions = [],
  subCategoryOptions = [],
  onSelectRow,
  onSelectAll,
  onAddRow,
  onDuplicateRows,
  onDeleteRows,
  onShowColumnModal,
  onShowBulkEditModal,
  onCellEdit,
  onCellSave,
  onOpenCategoryModal,
  editingCell,
  editValue,
  setEditValue,
  setEditingCell,
}) => {
  const hasSelection = selectedCount > 0;

  return (
    <div className="space-y-3">
      {/* Toolbar — the bulk-action zone is visually segmented from passive
       * actions so the selection-scoped controls feel like a temporary
       * "selection mode" affordance (Linear/Notion pattern). */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2 shadow-sm">
        <RowCountPopover
          label="Rows to add"
          confirmLabel={{ text: (n) => `Add ${n}`, onConfirm: onAddRow }}
          trigger={
            <Button variant="outline" size="sm" className="h-8 gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Add row
            </Button>
          }
        />
        <RowCountPopover
          label="Duplicate count"
          confirmLabel={{ text: (n) => `Duplicate ${n}`, onConfirm: onDuplicateRows }}
          trigger={
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
              disabled={selectedCount === 0}
              title={selectedCount === 0 ? "Select rows to duplicate" : undefined}
            >
              <Copy className="h-3.5 w-3.5" />
              Duplicate
            </Button>
          }
        />

        <span className="mx-1 h-5 w-px bg-border" aria-hidden />

        <div
          className={cn(
            "flex items-center gap-1.5 rounded-lg border px-1.5 py-1 transition-colors",
            hasSelection
              ? "border-primary/30 bg-primary/5"
              : "border-transparent"
          )}
        >
          {hasSelection && (
            <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary">
              <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                {selectedCount}
              </span>
              selected
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasSelection}
            onClick={onShowBulkEditModal}
            className="h-7 gap-1.5 text-xs hover:bg-primary/10"
            title={
              hasSelection
                ? `Apply the same value to ${selectedCount} row(s)`
                : "Select rows to bulk-edit"
            }
          >
            <Pencil className="h-3.5 w-3.5" />
            Bulk edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasSelection}
            onClick={onDeleteRows}
            className={cn(
              "h-7 gap-1.5 text-xs",
              hasSelection &&
                "hover:bg-destructive/10 hover:text-destructive"
            )}
            title={hasSelection ? `Delete ${selectedCount} row(s)` : "Select rows to delete"}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onShowColumnModal}
          className="ml-auto h-8 gap-1.5"
        >
          <Columns3 className="h-3.5 w-3.5" />
          Columns
        </Button>
      </div>

      {/* Data Table — wrapped in a refined card (rounded-xl + shadow-sm) for
       * the SaaS dashboard feel. The <thead> sticks via top-0 inside this
       * card; <PageBody> remains the single y-scroll region. */}
      <div className="overflow-x-auto rounded-xl border bg-card shadow-sm">
          <table className="w-full border-collapse text-sm product-table">
            <thead className="sticky top-0 z-[3]">
              <tr className="border-b bg-muted/30 backdrop-blur">
                <th className="w-[50px] py-3">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 cursor-pointer accent-primary"
                      checked={
                        selectedRows.size === products.length &&
                        products.length > 0
                      }
                      onChange={onSelectAll}
                    />
                  </div>
                </th>
                {columns
                  .filter((column) => visibleColumns.has(column.key))
                  .map((column) => (
                    <th
                      key={column.key}
                      data-col={column.key}
                      className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      <span className="inline-flex items-center gap-1">
                        {column.label}
                        {requiredFields.includes(column.key) && (
                          <span
                            className="inline-block h-1 w-1 rounded-full bg-destructive"
                            title="Required field"
                            aria-label="Required"
                          />
                        )}
                      </span>
                    </th>
                  ))}
              </tr>
            </thead>

            <tbody>
              {isLoadingData ? (
                <tr>
                  <td
                    colSpan={visibleColumns.size + 1}
                    className="text-center py-6"
                  >
                    <div
                      className="inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"
                      role="status"
                    />
                    <div className="mt-2 text-sm text-muted-foreground">Loading data…</div>
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const rowErrorMessage = rowErrors.get(product.id);
                  const isSelected = selectedRows.has(product.id);
                  return (
                  <tr
                    key={product.id}
                    className={cn(
                      "editable-row group border-b last:border-b-0 transition-colors",
                      isSelected
                        ? "bg-primary/[0.04] hover:bg-primary/[0.07]"
                        : "hover:bg-muted/40",
                      rowErrorMessage && "bulk-row-error"
                    )}
                  >
                    <td className="relative px-3 py-3">
                      {isSelected && (
                        <span
                          className="absolute inset-y-0 left-0 w-0.5 bg-primary"
                          aria-hidden
                        />
                      )}
                      <div className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          className="w-4 h-4 cursor-pointer accent-primary"
                          checked={isSelected}
                          onChange={() => onSelectRow(product.id)}
                        />
                        {rowErrorMessage && (
                          <AlertTriangle
                            className="h-4 w-4 text-nysmah-danger cursor-help shrink-0"
                            title={`${rowErrorMessage}\n\nFix the row and try again.`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    </td>

                    {columns
                      .filter((column) => visibleColumns.has(column.key))
                      .map((column) => {
                        const isEditingSelect =
                          editingCell?.row === product.id &&
                          editingCell?.column === column.key &&
                          column.key in SELECT_OPTION_GETTERS;
                        const rowErrors = validationErrors.get(product.id);
                        const isCellMissing = rowErrors && rowErrors.has(column.key);
                        const isDescriptionCol = HTML_DISPLAY_FIELDS.has(column.key);

                        return (
                          <td
                            key={column.key}
                            data-col={column.key}
                            className={cn(
                              "px-3 py-3",
                              isEditingSelect && "td-select-edit",
                              isCellMissing && "cell-validation-error"
                            )}
                            onClick={() => {
                              // Already editing this cell — ignore bubbled
                              // clicks from the inline editor so we don't
                              // reset its in-progress value.
                              if (
                                editingCell?.row === product.id &&
                                editingCell?.column === column.key
                              ) {
                                return;
                              }
                              if (column.key === "category") {
                                onOpenCategoryModal(product);
                              } else {
                                onCellEdit(
                                  product.id,
                                  column.key,
                                  product[column.key]
                                );
                              }
                            }}
                          >
                            {isDescriptionCol ? (
                              <DescriptionCell
                                value={product[column.key]}
                                label={column.label}
                                isRtl={column.key === "ar_description"}
                                isEditing={
                                  editingCell?.row === product.id &&
                                  editingCell?.column === column.key
                                }
                                editValue={editValue}
                                setEditValue={setEditValue}
                                onSave={() => onCellSave()}
                                onCancel={() => {
                                  setEditingCell(null);
                                  setEditValue("");
                                }}
                              />
                            ) : editingCell?.row === product.id &&
                            editingCell?.column === column.key ? (
                              column.key in SELECT_OPTION_GETTERS ? (
                                (() => {
                                  const opts = SELECT_OPTION_GETTERS[
                                    column.key
                                  ]({
                                    brandOptions,
                                    occasionOptions,
                                    colorOptions,
                                    sizeOptions,
                                    typeOptions,
                                    subCategoryOptions,
                                  });
                                  const clearable =
                                    CLEARABLE_SELECT_COLUMNS.has(column.key);
                                  // Radix Select shows the placeholder when
                                  // the value doesn't match any item — exactly
                                  // what we want for legacy comma-separated
                                  // values from the old multi-select brand /
                                  // occasion data.
                                  const currentValue =
                                    column.key === "is_b2b"
                                      ? coerceB2b(editValue)
                                      : editValue;
                                  const matched = opts.find(
                                    (o) => o.value === currentValue
                                  );
                                  return (
                                    <Select
                                      defaultOpen
                                      value={matched ? matched.value : undefined}
                                      onValueChange={(v) => {
                                        const final =
                                          v === SELECT_CLEAR_SENTINEL ? "" : v;
                                        setEditValue(final);
                                        onCellSave(final);
                                      }}
                                      onOpenChange={(open) => {
                                        // Closing without picking — drop edit
                                        // mode without committing a no-op edit
                                        // that would inflate the unsaved-count.
                                        if (!open) {
                                          setEditingCell(null);
                                          setEditValue("");
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="h-8 text-sm">
                                        <SelectValue placeholder="Select…" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {clearable && (
                                          <SelectItem
                                            value={SELECT_CLEAR_SENTINEL}
                                            className="italic text-muted-foreground"
                                          >
                                            — None —
                                          </SelectItem>
                                        )}
                                        {opts.map((opt) => (
                                          <SelectItem
                                            key={opt.value}
                                            value={opt.value}
                                          >
                                            {opt.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  );
                                })()
                              ) : (
                                <input
                                  className="form-control popup-input-main"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") onCellSave();
                                    if (e.key === "Escape") {
                                      setEditingCell(null);
                                      setEditValue("");
                                    }
                                  }}
                                  onBlur={() => onCellSave()}
                                  autoFocus
                                />
                              )
                            ) : (
                              <span className="text-sm text-foreground">
                                {column.key === "image" ? (
                                  product.image_urls?.["200w"] || product.image_urls?.["400w"] || product.image_urls?.["800w"] ? (
                                    <span className="inline-flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg border bg-muted/30 ring-1 ring-inset ring-border/40">
                                      <img
                                        src={product.image_urls["200w"] || product.image_urls["400w"] || product.image_urls["800w"]}
                                        alt="product"
                                        className="max-h-full max-w-full object-contain"
                                      />
                                    </span>
                                  ) : (
                                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-lg border border-dashed bg-muted/20 text-[10px] uppercase tracking-wide text-muted-foreground">
                                      No image
                                    </span>
                                  )
                                ) : column.key === "reference_link" ? (
                                  product[column.key] ? (
                                    <a
                                      href={product[column.key]}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-[11px] font-medium text-nysmah-info hover:bg-accent"
                                    >
                                      View link
                                    </a>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )
                                ) : (
                                  product[column.key] === null ||
                                  product[column.key] === undefined ||
                                  String(product[column.key]).trim() === "" ? (
                                    <span className="text-muted-foreground/60">—</span>
                                  ) : column.key === "is_b2b" ? (
                                    coerceB2b(product[column.key]) === "yes"
                                      ? "Yes"
                                      : coerceB2b(product[column.key]) === "no"
                                        ? "No"
                                        : String(product[column.key])
                                  ) : HTML_DISPLAY_FIELDS.has(column.key) ? (
                                    stripHtmlForDisplay(String(product[column.key]))
                                  ) : (
                                    String(product[column.key])
                                  )
                                )}
                              </span>
                            )}
                          </td>
                        );
                      })}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
    </div>
  );
};

// Memo'd: re-renders only when one of its props actually changes by reference.
// Parent (PendingProducts) memoizes dropdown option arrays + wraps every
// callback prop in useCallback, so toast/modal/unrelated state changes no
// longer cascade into the table.
export default memo(PendingProductsTable);
